(function () {
  'use strict';

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      if (!this.bound) {
        this.bound = true;
        this.controller = null;
        this.addEventListener('click', (e) => {
          const link = e.target.closest('.facets__chip, [data-facet-clear]');
          if (!link) return;
          e.preventDefault();
          this.pendingFocus = link.matches('[data-facet-clear]') ? 'clear' : 'chips';
          this.render(link.href);
        });
        this.onPopState = (e) => {
          if (e.state && e.state.url) this.render(window.location.href, false);
        };
        window.addEventListener('popstate', this.onPopState);
        if (window.history && history.replaceState && !(history.state && history.state.url)) {
          history.replaceState({ url: window.location.href }, '', window.location.href);
        }
      }
      this.init();
    }

    disconnectedCallback() {
      if (this.onPopState) window.removeEventListener('popstate', this.onPopState);
      this.bound = false;
    }

    init() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.debounceTimer = null;

      this.querySelectorAll('[data-facets-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => this.togglePanel(btn));
      });

      this.form.addEventListener('change', (e) => {
        const delay = e.target.type === 'number' ? 500 : 0;
        clearTimeout(this.debounceTimer);
        if (this.controller) this.controller.abort();
        this.debounceTimer = setTimeout(() => this.apply(), delay);
      });

      this.form.addEventListener('input', (e) => {
        if (e.target.type !== 'number') return;
        clearTimeout(this.debounceTimer);
        if (this.controller) this.controller.abort();
        this.debounceTimer = setTimeout(() => this.apply(), 600);
      });

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.apply();
      });

      this.setupPriceSliders();
    }

    setupPriceSliders() {
      this.querySelectorAll('[data-price-slider]').forEach((slider) => {
        const price = slider.parentElement.querySelector('.facets__price');
        const minField = price && price.querySelector('input[name$=".gte"]');
        const maxField = price && price.querySelector('input[name$=".lte"]');
        const thumbMin = slider.querySelector('[data-price-thumb="min"]');
        const thumbMax = slider.querySelector('[data-price-thumb="max"]');
        const fill = slider.querySelector('[data-price-fill]');
        if (!minField || !maxField || !thumbMin || !thumbMax) return;

        const ceiling = Number(thumbMax.max) || 0;
        if (ceiling <= 0) return;

        const paint = () => {
          const lo = Number(thumbMin.value);
          const hi = Number(thumbMax.value);
          fill.style.left = `${(lo / ceiling) * 100}%`;
          fill.style.width = `${((hi - lo) / ceiling) * 100}%`;
        };

        const clamp = (moved) => {
          let lo = Number(thumbMin.value);
          let hi = Number(thumbMax.value);
          if (lo > hi) {
            if (moved === thumbMin) { lo = hi; thumbMin.value = String(lo); }
            else { hi = lo; thumbMax.value = String(hi); }
          }
          return { lo, hi };
        };

        const push = (commit, moved) => {
          const { lo, hi } = clamp(moved);
          minField.value = lo > 0 ? String(lo) : '';
          maxField.value = hi < ceiling ? String(hi) : '';
          paint();
          if (commit) {
            minField.dispatchEvent(new Event('change', { bubbles: true }));
          }
        };

        thumbMin.addEventListener('input', () => push(false, thumbMin));
        thumbMax.addEventListener('input', () => push(false, thumbMax));
        thumbMin.addEventListener('change', () => push(true, thumbMin));
        thumbMax.addEventListener('change', () => push(true, thumbMax));

        const syncFromFields = () => {
          const lo = Math.max(0, Math.min(ceiling, Number(minField.value) || 0));
          const hi = Math.max(0, Math.min(ceiling, Number(maxField.value) || ceiling));
          thumbMin.value = String(Math.min(lo, hi));
          thumbMax.value = String(Math.max(lo, hi));
          paint();
        };
        minField.addEventListener('input', syncFromFields);
        maxField.addEventListener('input', syncFromFields);

        slider.hidden = false;
        paint();
      });
    }

    togglePanel(btn) {
      const panel = this.querySelector('.facets__panel');
      if (!panel) return;
      const open = panel.hasAttribute('hidden');
      panel.toggleAttribute('hidden', !open);
      btn.setAttribute('aria-expanded', String(open));
      this.classList.toggle('is-open', open);
    }

    apply() {
      const params = new URLSearchParams(new FormData(this.form));
      Array.from(params.entries()).forEach(([key, value]) => {
        if (value === '') params.delete(key);
      });
      const base = window.location.pathname;
      this.render(`${base}?${params.toString()}`);
    }

    async render(url, pushState) {
      const target = document.querySelector('[data-facet-target]');
      if (target) target.classList.add('is-loading');

      if (this.controller) this.controller.abort();
      this.controller = typeof AbortController === 'function' ? new AbortController() : null;

      try {
        const res = await fetch(url, { signal: this.controller ? this.controller.signal : undefined });
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');

        const freshGrid = parsed.querySelector('[data-facet-target]');
        if (!freshGrid) {
          if (res.ok) {
            window.location.href = url;
            return;
          }
          const region = document.getElementById('A11yStatus');
          if (region) region.textContent = (window.theme && window.theme.strings && window.theme.strings.error) || '';
          if (this.pendingFocus) {
            const back = this.querySelector('[data-facets-toggle]');
            if (back) back.focus();
            this.pendingFocus = null;
          }
          return;
        }
        if (target) {
          target.innerHTML = freshGrid.innerHTML;
          if (window.theme && typeof window.theme.promoteHoverImages === 'function') window.theme.promoteHoverImages(target);
        }

        const freshFacets = parsed.querySelector('facet-filters');
        if (freshFacets) {
          const panelEl = this.querySelector('.facets__panel');
          const panelWasOpen = !!panelEl && !panelEl.hasAttribute('hidden');

          const active = document.activeElement;
          const memo = active && this.contains(active)
            ? { name: active.name, value: active.value, tag: active.tagName,
                sort: active.hasAttribute('data-facet-sort'),
                toggle: active.hasAttribute('data-facets-toggle'),
                thumb: active.dataset.priceThumb || null }
            : null;
          const openGroups = [...this.querySelectorAll('.facets__group')].map((d) => d.open);

          this.innerHTML = freshFacets.innerHTML;
          this.init();

          if (panelWasOpen) {
            const panel = this.querySelector('.facets__panel');
            const btn = this.querySelector('[data-facets-toggle]');
            if (panel) panel.removeAttribute('hidden');
            if (btn) btn.setAttribute('aria-expanded', 'true');
          }
          this.querySelectorAll('.facets__group').forEach((d, i) => {
            if (openGroups[i] !== undefined) d.open = openGroups[i];
          });

          if (memo) {
            let target = null;
            if (memo.toggle) target = this.querySelector('[data-facets-toggle]');
            else if (memo.sort) target = this.querySelector('[data-facet-sort]');
            else if (memo.thumb) target = this.querySelector(`[data-price-thumb="${CSS.escape(memo.thumb)}"]`);
            else if (memo.name) {
              target = this.querySelector(
                `[name="${CSS.escape(memo.name)}"][value="${CSS.escape(memo.value)}"]`
              ) || this.querySelector(`[name="${CSS.escape(memo.name)}"]`);
            }
            if (target) target.focus();
          }

          if (this.pendingFocus) {
            const after =
              this.querySelector('.facets__chip') ||
              this.querySelector('[data-facet-clear]') ||
              this.querySelector('[data-facets-toggle]');
            if (after) after.focus();
            this.pendingFocus = null;
          }
        }

        if (pushState !== false) window.history.pushState({ url }, '', url);

        const count = parsed.querySelector('[data-facet-count]');
        const region = document.getElementById('A11yStatus');
        if (count && region) region.textContent = count.textContent.trim();
      } catch (err) {
        if (err.name !== 'AbortError') window.location.href = url;
      } finally {
        if (target) target.classList.remove('is-loading');
      }
    }
  }

  customElements.define('facet-filters', FacetFilters);
})();
